/* eslint-disable max-lines */
// TODO: Break this file into generic cart helper function
/* eslint-disable @typescript-eslint/naming-convention */
// internal-deps
import { Storage, isAndroidPlatform, isiOSPlatform } from "@swiggy-private/core-ui";
import { STORAGE_TYPES } from "@swiggy-private/native-bridge";
import {
    recordNREvent,
    getBridgeSdk,
    GlobalHeaderState,
    DineoutCartType,
    getDineoutFlowType,
    NEWRELIC_TABLES,
} from "@swiggy-private/checkout-core";

//constants
import {
    BENEFITS_TEXT,
    CONVENIENCE_FEE_TYPE,
    DealCategory,
    DEFAULT_REWARDS_WIDGET,
    DINEOUT_CART_WEBVIEW_OBJECT_KEY,
    MIN_ANDROID_VERSION_FOR_LOCAL_CART,
    MIN_IOS_VERSION_FOR_LOCAL_CART,
    PREVIOUS_CART_ID,
    RUPEES_TEXT,
    SCAN_QR,
    SUPER_V3_BENEFITS_KEY,
    SUPER_V3_BENEFITS_TYPE,
    SWIGGY_USER_TYPE,
    UX4_PRIMARY_BLACK,
} from "@dineout-cart-client/constants/Constants";
import { CART_META_DATA_TYPE, CART_TYPE, DINEOUT_CART_PAYLOAD, SOURCE } from "@dineout-cart-client/constants/TextDict";
import { ERROR_STATUS_CODES } from "@dineout-cart-client/constants/Api";
import { CART_UI_WIDGETIDS } from "@dineout-cart-client/constants/TextDict";

// helpers
import {
    getAllBillEntries,
    getFormattedDiscounts,
    getTipValue,
    getTotalAmountWithoutTip,
    navigateToLink,
    TIP_CHARGE,
} from "@swiggy-private/dineout-payment-success-ui";
import { isEmpty } from "@swiggy-private/js-utils";

//interfaces
import { IWidget, SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import {
    ICharge,
    IDineoutCartResponseData,
    IDineoutLocalCart as ILocalCart,
    ISubscriptionWidget,
    IApplyCouponWidget,
    ICallout,
} from "@swiggy-private/checkout-core";
import { ILocalCartResponseV2 } from "@dineout-cart-client/interfaces/LocalCart";

import { IErrorMeta, IGetLatestCartResponseResult } from "@dineout-cart-client/interfaces";
import { ITypographyV2 } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { CartMetaData, ITransformedDineoutCart, ISavingsText } from "@dineout-cart-client/interfaces/dineoutCartTypes";
import { ICtaSubTextFromCallouts } from "@dineout-cart-client/components/table-reservation/components/content";

type TipChargeMetadata = ICharge & {
    ctx?: {
        displayName?: string;
        description?: string;
    };
};

const getCartTipTitle = (charges?: ICharge[]): string => {
    const tipData = charges?.find((charge: ICharge) => charge?.type === TIP_CHARGE) as TipChargeMetadata | undefined;
    return tipData?.ctx?.displayName || "";
};

const getCartTipSubText = (charges?: ICharge[]): string => {
    const tipData = charges?.find((charge: ICharge) => charge?.type === TIP_CHARGE) as TipChargeMetadata | undefined;
    return tipData?.ctx?.description || "";
};

export const getDineoutCartData = async (): Promise<string | undefined | null> => {
    try {
        const response = await getBridgeSdk().storage({
            type: STORAGE_TYPES.GET,
            data: {
                [DINEOUT_CART_WEBVIEW_OBJECT_KEY]: "",
            },
        });

        const data = response?.data?.[DINEOUT_CART_WEBVIEW_OBJECT_KEY];
        if (!data) {
            return Promise.resolve(null);
        }

        return Promise.resolve(data);
    } catch (e) {
        return Promise.resolve(null);
    }
};

export const getBillDetailData = (cart: ITransformedDineoutCart, enteredAmount: number) => {
    if (cart) {
        const newDisc = cart.bill.discounts;
        const formattedDiscounts = getFormattedDiscounts(newDisc);
        const tip = getTipValue(cart.bill.charges);
        const tipTitle = getCartTipTitle(cart.bill.charges);
        const tipSubText = getCartTipSubText(cart.bill.charges);
        const totalBillWithoutTip = getTotalAmountWithoutTip(tip, cart.bill.dineoutPricingInfo.billToPay || 0);
        const superData = cart.superData;
        let feeObject = null;
        cart?.bill?.charges?.forEach((item) => {
            if (item.type === CONVENIENCE_FEE_TYPE) {
                feeObject = item;
            }
        });
        const billEntryProps = {
            discountList: formattedDiscounts,
            subscription: { planId: superData?.subscriptionPlanId },
            feeObject,
            totalBillAmount: totalBillWithoutTip,
            toPayAmount: cart.bill.dineoutPricingInfo.billToPay || 0,
            tip,
            tipTitle,
            tipSubText,
            advanceAmount: cart.bill?.dineoutPricingInfo?.advanceAmount
                ? cart.bill?.dineoutPricingInfo?.advanceAmount
                : 0,
            dineCash: cart.bill?.dineoutPricingInfo?.totalRewardsRedeemed,
            gst: getTotalGstAmount(cart),
            enteredBillAmount: enteredAmount,
            isTableReservation: false,
            guestCount: 0,
            isCart: true,
            redeemable: false,
        };
        const billEntry = getAllBillEntries(billEntryProps);
        return billEntry;
    }
};

export const getCouponWidgetDetail = (doCartState: ITransformedDineoutCart): IApplyCouponWidget => {
    const couponInfo = doCartState?.applyCouponWidget;
    const applyCouponWidget = {
        subtitle: couponInfo?.subtitle,
        subtitleLogo: couponInfo?.subtitleLogo,
        title: couponInfo?.title,
        subtitleColour: couponInfo?.subtitleColour,
        coupon: doCartState?.coupon?.coupon,
        value: doCartState?.coupon?.amount,
    };
    return applyCouponWidget;
};
export const isDealOrderCategory = (category: string) =>
    category === DealCategory.BAU ||
    category === DealCategory.GIRF ||
    category === DealCategory.RESERVATION ||
    category === DealCategory.PRIME_SLOT;

export const getSavingTextInfo = (cartData: IDineoutCartResponseData): ISavingsText => {
    const { superData, subscriptionWidget, superSavingBottomWidget } = cartData;
    const isOneMember = cartData?.superData?.userStatus === SWIGGY_USER_TYPE.ONE_USER;
    const isDealOrder = isDealOrderCategory(cartData?.bill?.dealCategory);
    const subscription =
        cartData?.superSavingBottomWidget?.subscriptionData || cartData?.subscriptionWidget?.subscriptionData || {};
    return {
        title: !superData?.subscriptionPlanId
            ? subscriptionWidget?.line1?.labels?.[0].value
            : superSavingBottomWidget?.lines?.[0]?.labels?.[0].value,
        subtitle: !superData?.subscriptionPlanId
            ? subscriptionWidget?.line2?.labels?.[0].value
            : superSavingBottomWidget?.lines?.[1].labels?.[0].value,
        offerAvailText: !superData?.subscriptionPlanId
            ? subscriptionWidget?.line3?.labels?.[0].value
            : superSavingBottomWidget?.lines?.[2].labels?.[0].value,
        afterLogoText1: "",
        animationScreenTitle: "",
        userStatus: cartData?.superData?.userStatus,
        oneBenefitConditions: {
            showPlanCard: !isOneMember && subscription && !isDealOrder,
        },
    };
};

export const fetchLocalCart = async (flowType: DineoutCartType | null) => {
    try {
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
        const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode);
        const versionCodeNumber = versionCode && !Number.isNaN(Number(versionCode)) ? Number(versionCode) : 0;

        // Check if version is below threshold
        const isVersionBelowThreshold =
            (isAndroidPlatform(platform) && versionCodeNumber <= MIN_ANDROID_VERSION_FOR_LOCAL_CART) ||
            (isiOSPlatform(platform) && versionCodeNumber <= MIN_IOS_VERSION_FOR_LOCAL_CART);

        // Below block is a hack to get the local cart data for bill payment cart on app platform, which will be removed in FFs, hence not using it for Web
        // Only execute this block if version is below threshold, otherwise use the new bridge function
        if (isBillPaymentCart(flowType) && isVersionBelowThreshold) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                type: "FETCH_LOCAL_CART_FOR_BILL_PAYMENT",
            });

            const storageInfo = await getDineoutCartData();
            const localBillPaymentCart = storageInfo ? JSON.parse(storageInfo) : null;
            const {
                restaurantId,
                restaurantID,
                couponCode = null,
                totalBillAmount,
                restaurantTip,
                rewardsWidget,
                distanceFromRx,
                ticketCtx,
                posCtx,
                cartType,
                payBillSource,
                paymentFlow,
                parentOrderId,
            } = localBillPaymentCart;
            const updatedLocalCart: ILocalCart = {
                cartMetaData: {
                    couponCode: couponCode || null,
                    dealCategory: localBillPaymentCart.dealCategory || "",
                    requestTimestamp: Date.now() / 1000,
                    restaurantId: restaurantId ? restaurantId : restaurantID,
                    billAmount: totalBillAmount,
                    type: CART_META_DATA_TYPE,
                    restaurantTip: restaurantTip || 0,
                    rewardsWidget: rewardsWidget || {
                        rewardsRedeemed: false,
                        redeemableRewards: 0,
                    },
                    ticketCtx: ticketCtx ? JSON.parse(ticketCtx) : undefined,
                    subscriptionPlanId: null,
                    posCtx: posCtx ? JSON.parse(posCtx) : undefined,
                    tableId: localBillPaymentCart?.tableId || undefined,
                    ...(!isEmpty(parentOrderId) && { parentOrderId }),
                },
                cartType: cartType || CART_TYPE,
                items: localBillPaymentCart.items || [],
                location: {
                    latitude: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat),
                    longitude: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng),
                },
                source: SOURCE,
                distanceFromRx,
                payBillSource,
                paymentFlow,
            };

            return updatedLocalCart;
        }

        /* Flow type is required for the dineout cart bridge functions, so that app can identify which cart flow is being used e.g. table reservation, event booking or bill payment */
        /* If we don't pass the flow type, the bridge functions will not be able to identify which cart flow to update/delete 
        /* and this might create issues when the user is using multiple carts in the same app session */
        const bridgePayload: { flowType: string; useV2Structure?: boolean } = {
            flowType: getDineoutFlowType(flowType),
        };
        if (isStealStoreCart(flowType)) {
            bridgePayload.useV2Structure = true;
        }
        return getBridgeSdk().dineoutGetLocalCart(bridgePayload);
    } catch (error) {
        console.error("Error fetching local cart data:", error); // Log the error for debugging
        recordNREvent({
            eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_LOCAL_CART_ERROR,
            error: "bridge_call_failure_fetchLocalCart",
            errorMessage: JSON.stringify(error),
            cartType: flowType ?? "",
        });
        return null;
    }
};

export const getLocalDineoutCart = (): Promise<ILocalCart> => {
    const cartPayload = Storage.getItem(DINEOUT_CART_PAYLOAD) as ILocalCart;
    return Promise.resolve(cartPayload);
};

export const isBillPaymentCart = (cartType: DineoutCartType | null) => {
    return cartType === DineoutCartType.BILL_PAYMENT_CART;
};

export const isTableReservationCart = (cartType: DineoutCartType | null) => {
    return cartType === DineoutCartType.TABLE_RESERVATION_CART;
};

export const isEventReservationCart = (cartType: DineoutCartType | null) => {
    return cartType === DineoutCartType.EVENT_BOOKING_CART;
};

export const isStealStoreCart = (cartType: DineoutCartType | null) => {
    return cartType === DineoutCartType.STEAL_STORE_CART;
};

export const isScenesFullPaymentCart = (cartType: DineoutCartType | null) => {
    return cartType === DineoutCartType.SCENES_FULL_PAYMENT;
};

export const isPaymentFlowScanQR = (paymentFlow: string) => {
    return paymentFlow === SCAN_QR;
};

export const getIsQRInvalid = (errorPayload: IErrorMeta | undefined) => {
    return errorPayload?.statusCode === ERROR_STATUS_CODES.QR_INVALID;
};

export const getTipAmount = (charges?: ICharge[]): number => {
    const tipData = charges?.find((charge: ICharge) => charge?.type === TIP_CHARGE);
    return tipData?.value || 0;
};

export const getSubscriptionBenefitsData = (
    savingText: ISavingsText,
    subscriptionData: ISubscriptionWidget,
    backgroundImage?: string,
) => {
    return {
        currency: RUPEES_TEXT,
        discount_message: "",
        hierarchy: 0,
        icon: "",
        info_text: "",
        intermediateText: "",
        key: SUPER_V3_BENEFITS_KEY,
        type: SUPER_V3_BENEFITS_TYPE,
        value: "",
        display_text: savingText?.title,
        meta: {
            backgroundImage: backgroundImage,
            headerColor: subscriptionData?.line1?.labels[0]?.color || "",
            showBorder: false,
            autoScaleWidth: true,
            benefits: [
                {
                    sub_title: savingText?.subtitle,
                    show_shimmer: false,
                    color: UX4_PRIMARY_BLACK,
                    applyCustomClass: true,
                },
            ],
            title: subscriptionData?.savingBenefitsTitle,
            logo_ctx: {
                text: BENEFITS_TEXT,
                logo: subscriptionData?.savingBenefitsImage,
            },
            footer_comms: null,
        },

        sub_details: [],
        children: [],
    };
};

export const getErrorPayloadData = (
    errorPayload: IErrorMeta | undefined,
    handleErrorWidgetImpression: VoidFunction,
    handleErrorWidgetClick: VoidFunction,
) => {
    const customPadding = {
        top: 10,
        bottom: 18,
    };
    const titleVariant = "V2.heading2.neutral.bold" as keyof ITypographyV2; //we are not getting the variant from the BE.
    const onPrimaryButtonClick = () => {
        handleErrorWidgetClick();
        if (errorPayload?.ctaAction?.meta?.deeplink) {
            navigateToLink(errorPayload.ctaAction.meta.deeplink);
        }
    };

    return {
        pageImpressionEvent: handleErrorWidgetImpression,
        title: errorPayload?.statusTitle || "",
        titleVariant,
        subTitle: errorPayload?.statusMessage || "",
        primaryButton: {
            onClick: onPrimaryButtonClick,
            text: errorPayload?.ctaText || "",
        },
        hasCustomPadding: customPadding,
    };
};

export const getTotalGstAmount = (cartState?: IDineoutCartResponseData | ITransformedDineoutCart | null): number =>
    cartState?.bill?.dineoutPricingInfo?.gst?.[0]?.totalGST || 0;

export const getCtaSubTextFromCallouts = (
    callouts?: ICallout[],
    title?: string,
): ICtaSubTextFromCallouts | undefined => {
    const ctaCallout = callouts?.find((callout) => callout?.title === title);
    return !ctaCallout ? undefined : { text: ctaCallout?.body, icon: ctaCallout?.icon };
};

export const getCancellationWidget = (widgets: IWidget[] | undefined): IWidget | undefined => {
    if (isEmpty(widgets)) return;
    return widgets.find(
        (widget) => widget.widgetInfo?.widgetType === CART_UI_WIDGETIDS.PREBOOK_PREPAID_CANCELLATION_WIDGET,
    );
};

export const getDelayedActionToastWidget = (widgets: IWidget[] | undefined): IWidget | undefined => {
    if (isEmpty(widgets)) return;
    return widgets.find(
        (widget) => widget.widgetInfo?.widgetType === CART_UI_WIDGETIDS.DINEOUT_DELAYED_ACTION_TOAST_WIDGET,
    );
};

export const getLatestCartResponse = (localCart?: ILocalCartResponseV2): IGetLatestCartResponseResult => {
    const latestCartCallTimestamp = localCart?.latestCartCallTimestamp;
    const thresholdInMS = localCart?.cartResponseThresholdInMs ?? 0;

    // Cache is expired when current time has passed (timestamp + threshold)
    const cacheExpired = isEmpty(latestCartCallTimestamp) || Date.now() >= latestCartCallTimestamp + thresholdInMS;

    const latestCartResponse = localCart?.latestCartResponse;

    return {
        cacheExpired,
        latestCartResponse,
    } as IGetLatestCartResponseResult;
};

export const getCartId = (cartResponse?: IDineoutCartResponseData | null): string => {
    return String(cartResponse?.cartId ?? "");
};

export const isCartIdSameAsPreviousCartId = (cartId: string) => cartId === Storage.getItem(PREVIOUS_CART_ID);

export const createUpdatedLocalCart = (
    baseCart: ILocalCart,
    stateMeta:
        | Pick<
              CartMetaData,
              "couponCode" | "restaurantTip" | "rewardsWidget" | "isPartialPaymentApplied" | "specialRequestInfo"
          >
        | null
        | undefined,
): ILocalCart => ({
    ...baseCart,
    cartMetaData: {
        ...baseCart.cartMetaData,
        couponCode: stateMeta?.couponCode ?? null,
        restaurantTip: stateMeta?.restaurantTip ?? 0,
        rewardsWidget: stateMeta?.rewardsWidget ?? DEFAULT_REWARDS_WIDGET,
        isPartialPaymentApplied: stateMeta?.isPartialPaymentApplied,
        specialRequestInfo:
            stateMeta?.specialRequestInfo ?? (baseCart.cartMetaData as CartMetaData | undefined)?.specialRequestInfo,
    } as CartMetaData,
});

export const isEventReservationFlow = (cartType: DineoutCartType) => {
    return cartType === DineoutCartType.EVENT_BOOKING_CART;
};
